import { ExtractedReportData } from "./reportDataExtractor";

export type ReportFilterKind = "party" | "item";
const patterns = {
  party: /party|customer|supplier|account name/i,
  item: /item|product/i,
};

export function filterColumns(report: ExtractedReportData, kind: ReportFilterKind) {
  return report.columns.flatMap((column, index) => patterns[kind].test(column.header) ? [index] : []);
}

export function filterOptions(report: ExtractedReportData, kind: ReportFilterKind): string[] {
  const indices = filterColumns(report, kind);
  const values = indices.length
    ? report.rows.flatMap(row => indices.map(index => row[index]))
    : report.summaryItems.filter(item => patterns[kind].test(item.label)).map(item => item.value);
  return Array.from(new Set(values.filter(value => value && !/^total\b/i.test(value)))).sort();
}

export function filterReport(report: ExtractedReportData, party: string, item: string): ExtractedReportData {
  if (!party && !item) return report;
  const selections: [ReportFilterKind, string][] = [["party", party], ["item", item]];
  const rows = report.rows.filter(row => {
    // Whole-report totals/opening balances must not be represented as filtered totals.
    if (row.some(value => /^(grand\s+total|total|opening balance|closing balance)\b/i.test(value))) return false;
    return selections.every(([kind, value]) => {
      if (!value) return true;
      const indices = filterColumns(report, kind);
      const isAvailableInReport = indices.length > 0 || filterOptions(report, kind).length > 0;
      // A selected filter must never silently send the unfiltered report.
      if (!isAvailableInReport) return false;
      return indices.length
        ? indices.some(index => row[index] === value)
        : filterOptions(report, kind).includes(value);
    });
  });
  return {
    ...report,
    rows,
    summaryItems: [
      ...report.summaryItems.filter(entry => !/total|balance|paid|received|remaining|difference/i.test(entry.label)),
      ...selections.filter(([, value]) => value).map(([kind, value]) => ({ label: kind === "party" ? "Party filter" : "Item filter", value })),
      { label: "Scope", value: "Filtered rows from the current report; whole-report totals omitted." },
    ],
  };
}
